import env from '@travel/env';
import { EMPTY_OBJECT } from 'constants/defaultValue';

import type { Store } from 'react-redux';

type RequestInterceptorOptions = {
  options: any;
  path: string;
  url: string;
  isThirdPartyApi: boolean;
  customParams?: { isTravelApi?: boolean; isStaticFile?: boolean };
};

const requestInterceptor =
  (store: Store) =>
  ({ options, path, url, isThirdPartyApi, customParams }: RequestInterceptorOptions) => {
    const state = store.getState();
    const { normalizedCookies } = state._httpRequest;
    const { currency, language, market } = state._i18n;

    let verbose = false;

    if (normalizedCookies) {
      verbose = 'trv-very-verbose' in normalizedCookies;
    }

    const isServer = typeof window === 'undefined';
    const { isTravelApi = false, isStaticFile = false } = customParams || {};
    const isMockEnv = env('UNIVERSAL_ENV_NAME') === 'MOCK';
    let basePath = '';

    if (isTravelApi) {
      const travelPath = env('UNIVERSAL_API_BASE_TRAVEL_PATH') ?? '';

      if (isServer) {
        const original = env('UNIVERSAL_API_BASE_URL_SERVER');

        basePath = original?.replace(/everest[^/]*.*$/, travelPath) ?? '';
      } else {
        const original = env('UNIVERSAL_API_BASE_URL_CLIENT');

        basePath = original?.replace('everest', travelPath) ?? '';
      }
    } else if (isStaticFile) {
      basePath = isServer
        ? (env('UNIVERSAL_STATIC_FILE_BASE_PATH_SERVER') ?? '')
        : (env('UNIVERSAL_STATIC_FILE_BASE_PATH_CLIENT') ?? '');
    } else {
      basePath = isServer
        ? (env('UNIVERSAL_API_BASE_URL_SERVER') ?? '')
        : (env('UNIVERSAL_API_BASE_URL_CLIENT') ?? '');
    }

    const resolvedUrl = `${basePath}${path}`;

    // To make sure that we will send user credential only if url is under the same origin for 3rd party
    const credentials = isThirdPartyApi ? 'same-origin' : options.credentials;
    const {
      item: { token },
    } = store.getState().authCode;

    const result = {
      url:
        (isThirdPartyApi || path.startsWith('http') || isMockEnv) && !isStaticFile
          ? url
          : resolvedUrl,
      options: {
        ...options,
        credentials,
        headers: isThirdPartyApi
          ? EMPTY_OBJECT
          : {
              'trv-device-id': normalizedCookies['trv-device-id']?.value,
              'trv-currency': currency,
              'trv-language': language,
              'trv-market': market?.marketCode,
              'trv-designation': 'standard',
              ...options.headers,
              ...(isTravelApi
                ? {
                    'trv-request-source': 'domestic',
                    'trv-rz-cookie': normalizedCookies['Rz']?.value,
                  }
                : {}),
            },
      },
      verbose,
    };

    if (token && !isThirdPartyApi) {
      result.options.headers.Authorization = token;
    }

    return result;
  };

export default requestInterceptor;
